'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsMobile } from './useIsMobile';
import { useIsMobileOverride } from '@jlg-website/editor/is-mobile';
import type { DesktopMenu, OpenMenu, TopDistance, Direction } from '@/components/pages/navbar/type';
import { toggleMenu } from '@/components/pages/navbar/menu-state';
import { FOOTER_FORM_ANCHOR_ID } from '@/components/pages/footer/constants';

const scrollYPositionTrigger = 130;
const navbarHiddenOffset = -200;
const bottomNavbarHiddenOffset = -100;
const LG_BREAKPOINT = 1024;
const ELEMENTS_TO_HIDE_NAVBOTTOM = ['form'];
const FORM_ANCHOR_SELECTOR = `#${FOOTER_FORM_ANCHOR_ID}`;
const FORM_INPUT_SELECTOR = 'input[type="text"]';
const FORM_HYDRATION_TIMEOUT_MS = 10000;
const FORM_SCROLL_OFFSET = 24;
const FORM_SCROLL_DURATION_MS = 900;
const FORM_SCROLL_MAX_DURATION_MS = 5000;
const FORM_SCROLL_SETTLED_PX = 2;
const FORM_SCROLL_RETARGET_PX = 8;

const DATA_MENU_SELECTOR = '[data-menu]';

function topFromContainer(isMobile: boolean, serverInitialTop: TopDistance): number {
  if (typeof window === 'undefined')
    return isMobile ? serverInitialTop.mobile : serverInitialTop.desktop;
  return window.innerWidth >= LG_BREAKPOINT ? serverInitialTop.desktop : serverInitialTop.mobile;
}

function someElementVisible(elements: Element[]): boolean {
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  return elements.some((el) => {
    const rect = el.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < viewportHeight;
  });
}

// Starts at full speed and decelerates into the target: the page has to be visibly
// moving on the frame after the click, so no ease-in phase.
function easeOutCubic(progress: number): number {
  return 1 - Math.pow(1 - progress, 3);
}

// The footer card streams a skeleton and swaps in the hydrated form engine, replacing the
// inputs inside it. Resolving the input at this point — rather than capturing one up front —
// is what keeps the focus off a node that is about to be thrown away. See JLG4-239.
function focusFormInputWhenHydrated(anchor: HTMLElement): () => void {
  const focusIfPresent = () => {
    const input = anchor.querySelector(FORM_INPUT_SELECTOR) as HTMLInputElement | null;
    if (!input) return false;

    input.focus({ preventScroll: true });
    return true;
  };

  if (focusIfPresent()) return () => undefined;

  let timeoutId = 0;

  const observer = new MutationObserver(() => {
    if (focusIfPresent()) stopWaiting();
  });

  const stopWaiting = () => {
    observer.disconnect();
    window.clearTimeout(timeoutId);
  };

  // Scoped to the card, not the document: React swaps this wrapper's children, never the
  // wrapper itself, so watching the whole body would wake this on every unrelated insertion
  // on the page for the length of the timeout.
  timeoutId = window.setTimeout(stopWaiting, FORM_HYDRATION_TIMEOUT_MS);
  observer.observe(anchor, { childList: true, subtree: true });

  return stopWaiting;
}

function scrollElementIntoViewWhileLayoutShifts(
  element: HTMLElement,
  focusWhenSettled: () => void
): () => void {
  const firstFrameAt = window.performance.now();
  let legStartedAt = 0;
  let legStartedY = 0;
  let legTargetY: number | null = null;
  let animationFrameId = 0;

  const scrollFrame = (now: number) => {
    // The one layout read of the frame. Re-measuring every frame is what lets content
    // mounting above the footer carry the destination with it, instead of leaving the
    // animation aimed at where the form used to be.
    const targetY = Math.max(
      0,
      window.scrollY + element.getBoundingClientRect().top - FORM_SCROLL_OFFSET
    );

    const hasMoved =
      legTargetY === null || Math.abs(targetY - legTargetY) > FORM_SCROLL_RETARGET_PX;
    if (hasMoved) {
      legStartedAt = now;
      legStartedY = window.scrollY;
      legTargetY = targetY;
    }

    const progress = Math.min(1, (now - legStartedAt) / FORM_SCROLL_DURATION_MS);
    const scrollYBefore = window.scrollY;

    // 'instant' is what makes the easing above ours to drive. Never 'auto': the public
    // layout sets scroll-behavior: smooth on <html> and per CSSOM-View 'auto' resolves
    // to it, so each frame handed the browser a fresh animation that the next frame
    // cancelled, and the page crawled a few pixels instead of moving. See JLG4-239.
    window.scrollTo({
      top: legStartedY + (targetY - legStartedY) * easeOutCubic(progress),
      behavior: 'instant',
    });

    // Scrolling does not move the element within the document, so this is identical to
    // remeasuring the rect — without the second forced reflow per frame.
    const distanceToTarget = Math.abs(targetY - window.scrollY);
    // Two ways to arrive. Closing the distance counts whenever it happens, including on the
    // very first frame when the reader clicked with the form already in place. Running out
    // of page counts only once the leg has finished: a form sitting closer to the end of the
    // document than one viewport makes targetY unreachable — the browser clamps the scroll
    // and the distance never closes — but frame one moves nothing either (progress is still
    // 0), so without the progress guard every trip would end before it began. See JLG4-239.
    const isPinned = window.scrollY === scrollYBefore;
    const hasArrived = distanceToTarget <= FORM_SCROLL_SETTLED_PX || (progress === 1 && isPinned);
    const timedOut = now - firstFrameAt >= FORM_SCROLL_MAX_DURATION_MS;

    if (hasArrived || timedOut) {
      // The settle distance decides *when* to stop, not where to stop. Landing on the exact
      // target keeps the card at the offset rather than up to FORM_SCROLL_SETTLED_PX off it;
      // where the target is unreachable this clamps to the same place the page already sits.
      if (hasArrived) window.scrollTo({ top: targetY, behavior: 'instant' });
      focusWhenSettled();
      return;
    }

    animationFrameId = window.requestAnimationFrame(scrollFrame);
  };

  scrollFrame(firstFrameAt);
  return () => window.cancelAnimationFrame(animationFrameId);
}

export function useNavbar(serverInitialTop: TopDistance, minTop: TopDistance) {
  const isMobile = useIsMobile();
  const mobileOverride = useIsMobileOverride();
  const [initialTop, setInitialTop] = useState(() => topFromContainer(isMobile, serverInitialTop));
  const [isScrolled, setIsScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [top, setTop] = useState<number | null>(null);
  const [transitionToTop, setTransitionToTop] = useState('none');
  const [bottom, setBottom] = useState(bottomNavbarHiddenOffset);

  const ticking = useRef(false);
  const lastScrollY = useRef(0);
  const currentTop = useRef(initialTop);
  const cancelFormScroll = useRef<(() => void) | null>(null);

  const scrollDirection = useRef<Direction>('down');
  const elementsToHideNavBottomRef = useRef<Element[]>([]);
  const overrideTop =
    mobileOverride === null
      ? null
      : mobileOverride
        ? serverInitialTop.mobile
        : serverInitialTop.desktop;
  const effectiveInitialTop = overrideTop ?? initialTop;
  const effectiveTop = overrideTop ?? top;
  const effectiveIsScrolled = mobileOverride === null ? isScrolled : false;

  const isMenuOpen = openMenu !== null;

  const toggleMenuOpen = useCallback(() => {
    setOpenMenu((prev) => (prev === 'mobile' ? null : 'mobile'));
  }, []);

  const openDesktopMenu = useCallback((menu: DesktopMenu) => {
    setOpenMenu((prev) => toggleMenu(prev, menu));
  }, []);

  const handleScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;
    requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      const delta = scrollY - lastScrollY.current;
      lastScrollY.current = scrollY;

      const direction = delta >= 0 ? 'down' : 'up';

      if (direction !== scrollDirection.current) {
        scrollDirection.current = direction;
        setTransitionToTop(direction === 'up' ? 'top 0.3s ease-out' : 'none');
      }

      if (isMobile) {
        setBottom((prev) => {
          const next =
            direction === 'down' && !someElementVisible(elementsToHideNavBottomRef.current)
              ? 0
              : bottomNavbarHiddenOffset;
          return prev !== next ? next : prev;
        });
      }

      const minVal = isMobile ? minTop.mobile : minTop.desktop;
      const newTop =
        scrollY <= effectiveInitialTop
          ? effectiveInitialTop
          : Math.min(minVal, Math.max(navbarHiddenOffset, currentTop.current - delta));
      if (newTop !== currentTop.current) {
        currentTop.current = newTop;
        setTop(newTop);
      }
      if (newTop < 0) {
        // Closing the navbar on scroll should dismiss desktop dropdowns, but never
        // the mobile drawer — that stays open until the user closes it explicitly.
        setOpenMenu((prev) => (prev === 'mobile' ? prev : null));
      }

      const scrolledState = scrollY > scrollYPositionTrigger;
      setIsScrolled((prev) => (prev !== scrolledState ? scrolledState : prev));

      ticking.current = false;
    });
  }, [effectiveInitialTop, minTop.desktop, minTop.mobile, isMobile]);

  useEffect(() => {
    if (!isMobile) return;

    const selector = ELEMENTS_TO_HIDE_NAVBOTTOM.join(',');
    const visibleElements = new Set<Element>();
    const observedElements = new Set<Element>();

    elementsToHideNavBottomRef.current = [];

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleElements.add(entry.target);
          else visibleElements.delete(entry.target);
        });
        if (visibleElements.size > 0) setBottom(bottomNavbarHiddenOffset);
      },
      { threshold: 0 }
    );

    const observe = (el: Element) => {
      if (observedElements.has(el)) return;
      observedElements.add(el);
      elementsToHideNavBottomRef.current.push(el);
      const rect = el.getBoundingClientRect();
      const vh = window.visualViewport?.height ?? window.innerHeight;
      if (rect.bottom > 0 && rect.top < vh) visibleElements.add(el);
      intersectionObserver.observe(el);
    };

    document.querySelectorAll(selector).forEach(observe);

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          const el = node as Element;
          if (el.matches(selector)) observe(el);
          el.querySelectorAll(selector).forEach(observe);
        });
      });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [isMobile]);

  // Editor preview guard: mobileOverride is non-null only when this hook is rendered
  // inside the CMS PageEditor's PreviewFrame (IsMobileProvider context). Without this
  // guard, the effect reads the admin window's scroll position on mount and leaves
  // isScrolled=true, forcing the navbar into its scrolled/opaque style and creating
  // a visible gap below it in the editor canvas.
  useEffect(() => {
    if (mobileOverride !== null) return;

    lastScrollY.current = window.scrollY;
    currentTop.current = effectiveInitialTop;
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, effectiveInitialTop, mobileOverride]);

  useEffect(() => {
    const handleResize = () => {
      const newInitialTop =
        window.innerWidth >= LG_BREAKPOINT ? serverInitialTop.desktop : serverInitialTop.mobile;
      setInitialTop(newInitialTop);
      currentTop.current = newInitialTop;
      setTop(newInitialTop);
    };

    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, [serverInitialTop.desktop, serverInitialTop.mobile]);

  useEffect(() => {
    if (isMobile) document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      if (isMobile) document.body.style.overflow = '';
    };
  }, [isMenuOpen, isMobile]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target?.closest?.(DATA_MENU_SELECTOR)) {
        setOpenMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const scrollToForm = useCallback(() => {
    const anchor = document.querySelector(FORM_ANCHOR_SELECTOR) as HTMLElement | null;
    if (!anchor) return;

    // The CTA also sits inside the mobile drawer, which locks body scrolling while it
    // is open — the page cannot move anywhere until it closes.
    setOpenMenu(null);

    cancelFormScroll.current?.();

    let cancelFocusWait: (() => void) | null = null;
    const cancelScroll = scrollElementIntoViewWhileLayoutShifts(anchor, () => {
      cancelFocusWait = focusFormInputWhenHydrated(anchor);
    });

    cancelFormScroll.current = () => {
      cancelScroll();
      cancelFocusWait?.();
    };
  }, []);

  useEffect(() => {
    return () => {
      cancelFormScroll.current?.();
    };
  }, []);

  return {
    isMobile,
    isScrolled: effectiveIsScrolled,
    isMenuOpen,
    openMenu,
    toggleMenuOpen,
    openDesktopMenu,
    top: effectiveTop || undefined,
    transitionToTop,
    bottom,
    scrollToForm,
  };
}
